import { MatterSelectionModal } from "@/components/ui";
import {
  retrieveObjectMatterMembershipsQueryOptions,
  useUnlinkObjectFromMatters,
} from "@/lib/data/react-query/matters/matter-related-object";
import type { RelatedMatterObjectType } from "@/lib/data/requests/matters/matter-related-object";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

type SharedItemObjectType = Exclude<RelatedMatterObjectType, "incident">;

interface RelatedMatterObjectDeleteModalProps {
  currentMatterId: string;
  objectId: string;
  objectLabel: string;
  objectType: SharedItemObjectType;
  onCurrentMatterRemoved: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function RelatedMatterObjectDeleteModal({
  currentMatterId,
  objectId,
  objectLabel,
  objectType,
  onCurrentMatterRemoved,
  onOpenChange,
  open,
}: RelatedMatterObjectDeleteModalProps) {
  const [selectedMatterIds, setSelectedMatterIds] = useState<string[]>([]);
  const memberships = useQuery(
    retrieveObjectMatterMembershipsQueryOptions({
      objectType,
      objectId,
      currentMatterId,
      enabled: open,
    }),
  );
  const unlinkFromMatters = useUnlinkObjectFromMatters({ objectLabel });
  const matters = useMemo(
    () =>
      [...(memberships.data?.data.matters ?? [])]
        .sort((left, right) => {
          if (left.id === currentMatterId) return -1;
          if (right.id === currentMatterId) return 1;
          return (left.title ?? "").localeCompare(right.title ?? "");
        })
        .map((matter) => ({
          id: matter.id,
          title: `${matter.title || "Untitled matter"}${matter.id === currentMatterId ? " (Current)" : ""}`,
        })),
    [currentMatterId, memberships.data?.data.matters],
  );

  useEffect(() => {
    if (!open) {
      setSelectedMatterIds([]);
      return;
    }
    if (matters.length > 0) {
      setSelectedMatterIds(matters.map((matter) => matter.id));
    }
  }, [matters, open]);

  const selectedCount = selectedMatterIds.length;

  return (
    <MatterSelectionModal
      open={open}
      onOpenChange={onOpenChange}
      matters={matters}
      selectedMatterIds={selectedMatterIds}
      onSelectedMatterIdsChange={setSelectedMatterIds}
      title={`Delete ${objectLabel.toLowerCase()} from matters`}
      description={`Select every matter that should no longer contain this ${objectLabel.toLowerCase()}. If no matters remain, it will be permanently deleted.`}
      submitLabel={`Delete from ${selectedCount} ${selectedCount === 1 ? "matter" : "matters"}`}
      loading={memberships.isLoading || unlinkFromMatters.isPending}
      onSubmit={async () => {
        const result = await unlinkFromMatters.mutateAsync({
          currentMatterId,
          matterIds: selectedMatterIds,
          objectId,
          objectType,
        });
        if (result.unlinkedMatterIds.length === 0) return;

        onOpenChange(false);
        if (result.unlinkedMatterIds.includes(currentMatterId)) {
          onCurrentMatterRemoved();
        }
      }}
    />
  );
}
